import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import type { User } from '@supabase/supabase-js';
import { Handshake, XCircle, Loader2 } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { Button } from '../components/ui/Button';

export const PENDING_INVITE_KEY = 'pendingInviteToken';
export const PENDING_INVITE_TOAST_KEY = 'pendingInviteToast';

interface Props {
  user: User | null;
}

type Status = 'loading' | 'error';

// Heurística: se a conta foi criada há menos de 5 minutos, considera-se que
// o usuário acabou de se cadastrar via fluxo de convite e a amizade vira direta.
// Para usuários antigos, gera um friend_request pendente.
const NEW_USER_WINDOW_MS = 5 * 60 * 1000;

function isFreshlyCreated(user: User): boolean {
  const createdAt = user.created_at ? new Date(user.created_at).getTime() : 0;
  if (!createdAt) return false;
  return Date.now() - createdAt < NEW_USER_WINDOW_MS;
}

export default function InvitePage({ user }: Props) {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [status, setStatus] = useState<Status>('loading');
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    if (!token) {
      navigate('/');
      return;
    }
    if (!user) {
      localStorage.setItem(PENDING_INVITE_KEY, token);
      navigate('/');
      return;
    }
    accept(token, user);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, user]);

  async function accept(t: string, u: User) {
    const { data: invite } = await supabase
      .from('friend_invites')
      .select('*')
      .eq('token', t)
      .single();

    if (!invite) {
      setStatus('error');
      setErrorMsg('Convite inválido ou expirado.');
      return;
    }

    if (invite.inviter_id === u.id) {
      setStatus('error');
      setErrorMsg('Você não pode se adicionar como amigo.');
      return;
    }

    const { data: existing } = await supabase
      .from('friendships')
      .select('id')
      .eq('user_id', u.id)
      .eq('friend_id', invite.inviter_id)
      .maybeSingle();

    if (existing) {
      localStorage.removeItem(PENDING_INVITE_KEY);
      navigate('/album?tab=amigos');
      return;
    }

    const inviterName = invite.inviter_name || invite.inviter_email;

    if (isFreshlyCreated(u)) {
      // Usuário acabou de se cadastrar via convite: amizade direta.
      const accepterName = (u.user_metadata?.full_name as string) || u.email || '';
      await supabase.from('friendships').insert([
        {
          user_id: u.id,
          friend_id: invite.inviter_id,
          friend_email: invite.inviter_email,
          friend_name: inviterName,
        },
        {
          user_id: invite.inviter_id,
          friend_id: u.id,
          friend_email: u.email,
          friend_name: accepterName,
        },
      ]);
    } else {
      // Usuário existente: cria pedido de amizade pendente do inviter para o user.
      // TODO: revisar se o ideal é cancelar pedido pendente em direção contrária
      // (caso eu já tenha enviado pedido para o inviter, aceitar ambos automaticamente).
      const { data: existingReq } = await supabase
        .from('friend_requests')
        .select('id, status, from_user_id, to_user_id')
        .or(
          `and(from_user_id.eq.${invite.inviter_id},to_user_id.eq.${u.id}),` +
            `and(from_user_id.eq.${u.id},to_user_id.eq.${invite.inviter_id})`
        )
        .eq('status', 'pending')
        .maybeSingle();

      if (!existingReq) {
        await supabase.from('friend_requests').insert({
          from_user_id: invite.inviter_id,
          to_user_id: u.id,
          status: 'pending',
        });
      }

      try {
        localStorage.setItem(
          PENDING_INVITE_TOAST_KEY,
          `Pedido de amizade recebido de ${inviterName}.`
        );
      } catch {
        // ignore quota
      }
    }

    localStorage.removeItem(PENDING_INVITE_KEY);
    navigate('/album?tab=amigos');
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-green-700 px-4">
        <div className="bg-white rounded-2xl border border-ink-300 p-8 mx-4 text-center max-w-sm shadow-md">
          <XCircle size={48} className="mx-auto text-danger mb-4" strokeWidth={1.75} />
          <p className="font-display font-semibold text-h3 text-ink-900 mb-1">
            Convite com problema
          </p>
          <p className="font-body text-small text-ink-700 mb-5">{errorMsg}</p>
          <Button variant="primary" size="md" onClick={() => navigate('/album')}>
            Ir para o álbum
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-green-700">
      <div className="text-white text-center">
        <Handshake size={48} className="mx-auto mb-3" strokeWidth={1.75} />
        <p className="font-body text-small text-white/85 inline-flex items-center gap-2">
          <Loader2 size={14} className="animate-spin" />
          Processando convite...
        </p>
      </div>
    </div>
  );
}
